import {
	FileAudio,
	FileCode,
	FileImage,
	FileSpreadsheet,
	FileText,
	FileVideo,
	FileX,
	Paperclip,
} from "lucide-react";
import type { FC } from "react";

interface FileIconProps {
	mimeType: string;
	className?: string;
	size?: number;
	showLabel?: boolean;
	labelClassName?: string;
}

export const getFileTypeLabel = (mimeType: string): string => {
	const lowerMimeType = mimeType.toLowerCase();

	if (lowerMimeType.includes("pdf")) return "PDF";

	if (
		lowerMimeType.includes("msword") ||
		lowerMimeType.includes("wordprocessingml")
	) {
		return "Word";
	}

	if (
		lowerMimeType.includes("excel") ||
		lowerMimeType.includes("spreadsheet")
	) {
		return "Excel";
	}

	if (lowerMimeType.startsWith("image/")) {
		if (lowerMimeType.includes("jpeg") || lowerMimeType.includes("jpg"))
			return "JPEG";
		if (lowerMimeType.includes("png")) return "PNG";
		if (lowerMimeType.includes("gif")) return "GIF";
		if (lowerMimeType.includes("svg")) return "SVG";
		if (lowerMimeType.includes("webp")) return "WebP";
		return "Image";
	}

	if (lowerMimeType.startsWith("video/")) {
		if (lowerMimeType.includes("mp4")) return "MP4";
		if (lowerMimeType.includes("avi")) return "AVI";
		if (lowerMimeType.includes("mov")) return "MOV";
		return "Vidéo";
	}

	if (lowerMimeType.startsWith("audio/")) {
		if (lowerMimeType.includes("mp3")) return "MP3";
		if (lowerMimeType.includes("wav")) return "WAV";
		if (lowerMimeType.includes("ogg")) return "OGG";
		return "Audio";
	}

	if (lowerMimeType.includes("javascript")) return "JavaScript";
	if (lowerMimeType.includes("typescript")) return "TypeScript";
	if (lowerMimeType.includes("json")) return "JSON";
	if (lowerMimeType.includes("xml")) return "XML";
	if (lowerMimeType.includes("html")) return "HTML";
	if (lowerMimeType.includes("css")) return "CSS";
	if (lowerMimeType.includes("text/plain")) return "Texte";

	if (lowerMimeType.includes("zip")) return "ZIP";
	if (lowerMimeType.includes("rar")) return "RAR";
	if (lowerMimeType.includes("tar")) return "TAR";
	if (lowerMimeType.includes("gzip")) return "GZIP";

	return "Fichier";
};

const getIconByMimeType = (mimeType: string) => {
	const lowerMimeType = mimeType.toLowerCase();

	// PDF
	if (lowerMimeType.includes("pdf")) {
		return FileText;
	}

	if (
		lowerMimeType.includes("msword") ||
		lowerMimeType.includes("wordprocessingml")
	) {
		return FileText;
	}

	if (
		lowerMimeType.includes("excel") ||
		lowerMimeType.includes("spreadsheet")
	) {
		return FileSpreadsheet;
	}

	if (lowerMimeType.startsWith("image/")) {
		return FileImage;
	}

	if (lowerMimeType.startsWith("video/")) {
		return FileVideo;
	}

	if (lowerMimeType.startsWith("audio/")) {
		return FileAudio;
	}

	if (
		lowerMimeType.includes("text/") ||
		lowerMimeType.includes("javascript") ||
		lowerMimeType.includes("json") ||
		lowerMimeType.includes("xml") ||
		lowerMimeType.includes("html")
	) {
		return FileCode;
	}

	if (
		lowerMimeType.includes("zip") ||
		lowerMimeType.includes("rar") ||
		lowerMimeType.includes("tar") ||
		lowerMimeType.includes("gzip")
	) {
		return FileX;
	}

	return Paperclip;
};

const getIconColor = (mimeType: string) => {
	const lowerMimeType = mimeType.toLowerCase();

	if (lowerMimeType.includes("pdf")) return "text-red-500";
	if (
		lowerMimeType.includes("word") ||
		lowerMimeType.includes("wordprocessingml")
	)
		return "text-blue-500";
	if (
		lowerMimeType.includes("excel") ||
		lowerMimeType.includes("spreadsheet")
	)
		return "text-green-500";
	if (lowerMimeType.startsWith("image/")) return "text-purple-500";
	if (lowerMimeType.startsWith("video/")) return "text-pink-500";
	if (lowerMimeType.startsWith("audio/")) return "text-orange-500";
	if (lowerMimeType.includes("text/") || lowerMimeType.includes("code"))
		return "text-gray-600";

	return "text-gray-500";
};

export const FileIcon: FC<FileIconProps> = ({
	mimeType,
	className = "",
	size,
	showLabel = false,
	labelClassName = "",
}) => {
	const Icon = getIconByMimeType(mimeType);
	const colorClass = getIconColor(mimeType);
	const label = getFileTypeLabel(mimeType);

	if (showLabel) {
		return (
			<div className="flex items-center gap-2">
				<Icon
					className={`${colorClass} ${className}`}
					size={size}
					aria-hidden="true"
				/>
				<span className={`text-xs font-medium ${labelClassName}`}>
					{label}
				</span>
			</div>
		);
	}

	return (
		<Icon
			className={`${colorClass} ${className}`}
			size={size}
			aria-hidden="true"
		/>
	);
}; 
